import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

const HashGeneratorTool = () => {
  const [input, setInput] = useState('');
  const [algorithm, setAlgorithm] = useState('SHA-256');
  const [output, setOutput] = useState('');

  const generateHash = async () => {
    if (!input) {
      toast.error('Please enter text to hash');
      return;
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest(algorithm, data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      setOutput(hashHex);
      toast.success('Hash generated!');
    } catch (e) {
      toast.error('Failed to generate hash');
    }
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      toast.success('Copied!');
    }
  };

  return (
    <div className="space-y-4" data-testid="hash-generator-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Hash Generator</h3>
      </div>

      <Textarea
        placeholder="Enter text to hash..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className="min-h-[100px] bg-background/50"
        data-testid="input"
      />

      <div className="flex gap-2">
        <Select value={algorithm} onValueChange={setAlgorithm}>
          <SelectTrigger className="w-40 bg-background/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SHA-1">SHA-1</SelectItem>
            <SelectItem value="SHA-256">SHA-256</SelectItem>
            <SelectItem value="SHA-384">SHA-384</SelectItem>
            <SelectItem value="SHA-512">SHA-512</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={generateHash} className="flex-1" data-testid="generate-btn">
          Generate Hash
        </Button>
      </div>

      {output && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{algorithm} Hash</span>
            <Button variant="ghost" size="sm" onClick={copyOutput}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
          <Input
            value={output}
            readOnly
            className="font-mono text-xs bg-background/50"
            data-testid="output"
          />
        </div>
      )}
    </div>
  );
};

export default HashGeneratorTool;
